import React from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import logoIcon from "@/assets/regard-de-nature-logo.png";
import { FileText } from "lucide-react";
import { BRAND } from "@/config/brand";

interface EthicsCharterProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  triggerClassName?: string;
  asChild?: boolean;
  children?: React.ReactNode;
}

export const EthicsCharter = ({ open, onOpenChange, triggerClassName, asChild = true, children }: EthicsCharterProps) => {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetTrigger asChild={asChild}>
        {children || (
          <button className={triggerClassName || "hover:text-primary transition-colors text-left"}>
            Charte déontologique
          </button>
        )}
      </SheetTrigger>
      <SheetContent side="right" className="w-full sm:max-w-2xl">
        <SheetHeader className="space-y-4 pb-6 border-b">
          <div className="flex flex-col items-center gap-3">
            <img src={logoIcon} alt={BRAND.name.full} className="w-16 h-16" />
            <div className="text-center">
              <SheetTitle className="text-3xl font-cinzel text-primary">
                {BRAND.name.full}
              </SheetTitle>
              <p className="text-sm font-montserrat font-semibold text-secondary uppercase tracking-wider mt-1">
                {BRAND.baseline.full}
              </p>
            </div>
          </div>
          <SheetDescription className="text-center text-lg font-medium text-foreground flex items-center justify-center gap-2">
            <FileText className="w-5 h-5 text-primary" />
            Charte Déontologique
          </SheetDescription>
        </SheetHeader>

        <ScrollArea className="h-[calc(100vh-260px)] mt-6 pr-4 pb-12">
          <div className="space-y-6 text-sm">
            <section>
              <h3 className="text-lg font-semibold text-primary mb-3 border-b border-border pb-2">
                1. ACTE NON-THÉRAPEUTIQUE
              </h3>
              <div className="space-y-2 text-muted-foreground">
                <p>Le Praticien en Santé Naturelle Naturopathe (PSN) s'engage à respecter le diagnostic médical et les traitements en cours. Le médecin étant le seul habilité au diagnostic et au traitement des maladies.</p>
                <p>Le PSN accompagne les personnes sur des troubles fonctionnels. En cas de doute, il peut être amené à contacter le médecin.</p>
                <p className="font-medium text-foreground">Le PSN ne doit en aucun cas prendre en charge une personne présentant une douleur physique ou psychique, si elle n'a pas préalablement consulté son médecin.</p>
                <p>Dans le cas où la personne est suivie médicalement pour un problème spécifique, le PSN exigera un certificat médical indiquant que la pratique des techniques de bien-être et d'accompagnement ne sont pas contre-indiquées.</p>
                <p>Il devra également informer tous ses clients que les techniques de bien-être ainsi que les différents conseils ne sont pas à but thérapeutique.</p>
                <p>Le PSN collabore avec tous les professionnels de santé. Il orientera ses clients vers d'autres compétences médicales si cela s'avère nécessaire, dans une visée d'interdisciplinarité, et de meilleure prise en charge du patient.</p>
                <p>Le PSN ne réalise pas un travail de verbalisation, ni d'analyse, réservé au psychologue. Il utilise des techniques manuelles permettant de libérer les tensions et d'accueillir le ressenti du patient, dans un objectif de complémentarité avec le psychologue.</p>
                <p>Le PSN s'engage à prodiguer des conseils, uniquement dans l'intérêt seul du patient, et toujours avec son accord.</p>
              </div>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-primary mb-3 border-b border-border pb-2">
                2. MASSAGES ET TECHNIQUES MANUELLES DE DÉTENTE (CADRE FRANÇAIS)
              </h3>
              <div className="space-y-2 text-muted-foreground">
                <p>Les massages thérapeutiques et non-thérapeutiques sont définis par le code Français de la Santé (Art.3 du décret du 8 oct. 1996) et sont l'exclusivité des masseurs kinésithérapeutes et ne sont accessibles par aucune autre formation.</p>
                <p>Les méthodes manuelles relaxantes basées sur l'effleurage, le toucher et les pressions légères permettant au PSN d'apporter un réconfort, une détente et un accompagnement à la personne, ne sont pas des massages et ne comportent pas de mobilisations ou de stimulations méthodiques, mécaniques ou réflexes des tissus.</p>
                <p>Sont exclus des techniques manuelles le drainage lymphatique, le palper-rouler et les massages mécaniques, car seuls les masseurs kinésithérapeutes peuvent les pratiquer.</p>
                <p className="font-medium text-foreground">Toutes les techniques proposées par le PSN le sont dans un but préventif et d'accompagnement afin d'améliorer la qualité de vie des personnes. Elles ne sont en aucun cas à caractère sexuel ou thérapeutique.</p>
              </div>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-primary mb-3 border-b border-border pb-2">
                3. PUBLICITÉ
              </h3>
              <div className="space-y-2 text-muted-foreground">
                <p>En respect de la profession des praticiens de santé, le PSN ne devra pas faire de publicité évoquant la pratique de massages, ceux-ci pouvant être interprétés comme un acte de masso-kinésithérapie, dont seuls les masseurs kinésithérapeutes sont habilités à le réaliser (monopole).</p>
                <p>Il en est de même de l'accompagnement des personnes : nul PSN ne doit faire de publicité sur les éventuels bénéfices d'une technique de bien-être sur la prise en charge d'une maladie. Seul le médecin peut juger de l'évolution d'une pathologie, et de sa prise en charge.</p>
              </div>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-primary mb-3 border-b border-border pb-2">
                4. CONFIDENTIALITÉ
              </h3>
              <div className="space-y-2 text-muted-foreground">
                <p>Le PSN s'engage à la discrétion vis-à-vis de son client, à respecter sa vie privée, son intimité, et à préserver la confidentialité des séances.</p>
                <p className="font-medium text-foreground">Il est tenu au secret professionnel.</p>
              </div>
            </section>
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
};
